import React, { useCallback, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { deletePlanConfig, getPlanConfig, savePlanConfig } from "../../api/billing";
import { LoadState } from "../UserDetails/ui";
import type { AdminPlan, PlanConfigRow } from "./types";
import { ErrorText, Field, Modal, SaveButton, inputClass, secondaryButtonClass, useAction, useLoad } from "./ui";

/** Keys the DrawPost backend enforces; anything else is display-only. */
const KNOWN_KEYS: Record<string, string> = {
  posting_accounts: "Max connected social accounts (number). Enforced.",
  max_clients: "Max clients / brands (number or 'unlimited'). Enforced.",
  max_users: "Max team members (number or 'unlimited').",
  credits_per_month: "Display only — real credits come from the price.",
};

const KEY_PATTERN = /^[a-z0-9_]{1,64}$/;

type EditableRow = PlanConfigRow & { rowKey: string; isNew: boolean };

const toEditable = (rows: PlanConfigRow[]): EditableRow[] =>
  rows.map((r) => ({ ...r, rowKey: crypto.randomUUID(), isNew: false }));

function validateRows(rows: EditableRow[]): string | null {
  const keys = new Set<string>();
  for (const r of rows) {
    if (!KEY_PATTERN.test(r.key)) return `"${r.key || "(empty)"}" is not a valid key — use lowercase letters, digits and _.`;
    if (keys.has(r.key)) return `Key "${r.key}" is listed twice.`;
    keys.add(r.key);
    if (!r.label.trim()) return `Key "${r.key}" needs a label.`;
  }
  return null;
}

/** DrawPost plan settings (`drawpost_plan_configs`): limits and display values. */
export const PlanSettingsModal: React.FC<Readonly<{ plan: AdminPlan; onClose: () => void; onSaved: () => void }>> = ({
  plan,
  onClose,
  onSaved,
}) => {
  const [rows, setRows] = useState<EditableRow[] | null>(null);
  const load = useCallback(async () => {
    const data = await getPlanConfig(plan.id);
    setRows(toEditable(data));
    return data;
  }, [plan.id]);
  const { loading, error: loadError, reload } = useLoad(load);
  const { busy, error, setError, run } = useAction();

  const update = (rowKey: string, patch: Partial<EditableRow>) =>
    setRows((prev) => (prev ?? []).map((r) => (r.rowKey === rowKey ? { ...r, ...patch } : r)));

  const remove = async (row: EditableRow) => {
    if (row.isNew) {
      setRows((prev) => (prev ?? []).filter((r) => r.rowKey !== row.rowKey));
      return;
    }
    const removed = await run(() => deletePlanConfig(plan.id, row.key));
    if (removed) setRows((prev) => (prev ?? []).filter((r) => r.rowKey !== row.rowKey));
  };

  const save = async () => {
    const current = rows ?? [];
    if (current.length === 0) return onClose();
    const rowError = validateRows(current);
    if (rowError) return setError(rowError);
    const saved = await run(() =>
      savePlanConfig(
        plan.id,
        current.map((row) => ({
          key: row.key,
          value: row.value.trim(),
          valueType: row.valueType,
          label: row.label.trim(),
          description: row.description,
          displayOrder: row.displayOrder,
          isVisible: row.isVisible,
        }))
      )
    );
    if (saved) onSaved();
  };

  const addRow = () =>
    setRows((prev) => [
      ...(prev ?? []),
      {
        rowKey: crypto.randomUUID(),
        isNew: true,
        key: "",
        value: "",
        valueType: "number",
        label: "",
        description: null,
        displayOrder: (prev?.length ?? 0) + 1,
        isVisible: true,
      },
    ]);

  return (
    <Modal
      title={`${plan.name} — settings & limits`}
      onClose={onClose}
      busy={busy}
      width="xl"
      footer={
        <>
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onClose}>
            Close
          </button>
          <SaveButton busy={busy} disabled={!rows} onClick={save}>
            Save settings
          </SaveButton>
        </>
      }
    >
      {(loading || loadError) && !rows ? (
        <LoadState loading={loading} error={loadError} onRetry={reload} />
      ) : (
        <div className="space-y-3">
          {(rows ?? []).length === 0 && (
            <p className="text-sm text-gray-500">No settings yet — this plan has no limits.</p>
          )}
          {(rows ?? []).map((row) => (
            <div key={row.rowKey} className="grid grid-cols-2 gap-2 rounded-lg border border-gray-100 p-3 md:grid-cols-[1.2fr_1fr_1.4fr_0.8fr_auto] md:items-end">
              <Field label="Key" hint={KNOWN_KEYS[row.key]}>
                <input
                  className={inputClass}
                  value={row.key}
                  disabled={!row.isNew}
                  onChange={(e) => update(row.rowKey, { key: e.target.value.trim().toLowerCase() })}
                  placeholder="posting_accounts"
                />
              </Field>
              <Field label="Value">
                <input className={inputClass} value={row.value} onChange={(e) => update(row.rowKey, { value: e.target.value })} />
              </Field>
              <Field label="Label (shown to users)">
                <input className={inputClass} value={row.label} onChange={(e) => update(row.rowKey, { label: e.target.value })} />
              </Field>
              <Field label="Type">
                <select className={inputClass} value={row.valueType} onChange={(e) => update(row.rowKey, { valueType: e.target.value })}>
                  <option value="number">Number</option>
                  <option value="string">Text</option>
                  <option value="boolean">Yes/No</option>
                </select>
              </Field>
              <button
                type="button"
                aria-label="Remove setting"
                disabled={busy}
                onClick={() => void remove(row)}
                className="mb-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-30"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" className={secondaryButtonClass} onClick={addRow}>
            <Plus className="w-4 h-4" /> Add setting
          </button>
          <p className="text-xs text-gray-400">
            Removing an enforced limit (posting_accounts, max_clients) removes the limit — the plan becomes unlimited for it.
            Discounted copies use the settings of the plan they copy.
          </p>
          <ErrorText message={error} />
        </div>
      )}
    </Modal>
  );
};
